<script>
  //@ts-nocheck
// Default Values
const DEFAULT_NAME = 'total';
const DEFAULT_VALUE = 0;
const DEFAULT_FRACTION_DIGITS = 2;
const DEFAULT_CLASS_WRAPPER = 'currencyInput';
const DEFAULT_CLASS_UNFORMATTED = 'currencyInput__unformatted';
const DEFAULT_CLASS_FORMATTED = 'currencyInput__formatted';
const DEFAULT_CLASS_FORMATTED_POSITIVE = 'currencyInput__formatted--positive';
const DEFAULT_CLASS_FORMATTED_NEGATIVE = 'currencyInput__formatted--negative';
const DEFAULT_CLASS_FORMATTED_ZERO = 'currencyInput__formatted--zero';
const DEFAULT_LOCALE = 'en-US';
const DEFAULT_CURRENCY = 'USD';

import { onMount } from "svelte";
export let value = DEFAULT_VALUE;
export let locale = DEFAULT_LOCALE;
export let currency = DEFAULT_CURRENCY;
export let fractionDigits = DEFAULT_FRACTION_DIGITS;
export let name = DEFAULT_NAME;
export let id = void 0;
export let required = false;
export let disabled = false;
export let placeholder = DEFAULT_VALUE;
export let autocomplete = void 0;
export let isNegativeAllowed = true;
export let isZeroNullish = false;
export let inputClasses = null;
export let maxLength = 20;
export let onValueChange = () => {
};
let inputElement;
let inputTarget;
let formattedValue = "";
$: isNegative = value < 0;
$: isPositive = value > 0;
$: isZero = !isNegative && !isPositive;
$: value, setFormattedValue();
onMount(() => {
  setFormattedValue();
});
function formatCurrency(value2, maximumFractionDigits, minimumFractionDigits) {
  return new Intl.NumberFormat(locale, {
    currency,
    style: "currency",
    maximumFractionDigits: maximumFractionDigits || 0,
    minimumFractionDigits: minimumFractionDigits || 0
  }).format(value2);
}
function handleKeyDown(event) {
  const isDeletion = event.key === "Backspace" || event.key === "Delete";
  const isModifier = event.metaKey || event.altKey || event.ctrlKey;
  const isArrowKey = event.key === "ArrowLeft" || event.key === "ArrowRight";
  const isTab = event.key === "Tab";
  const isEnter = event.key === "Enter";
  const isInvalidCharacter = !/^\d|,|\.|-$/g.test(event.key);
  function isPunctuationDuplicated() {
    if (event.key !== "," && event.key !== ".") return false;
    if (isDecimalComma) return formattedValue.split(",").length >= 2;
    if (!isDecimalComma) return formattedValue.split(".").length >= 2;
    return false;
  }
  if (isPunctuationDuplicated() || !isDeletion && !isModifier && !isArrowKey && isInvalidCharacter && !isTab && !isEnter)
    event.preventDefault();
}
function handlePlaceholder(placeholder2) {
  if (typeof placeholder2 === "number")
    return formatCurrency(placeholder2, fractionDigits, fractionDigits);
  if (placeholder2 === null) return "";
  return placeholder2;
}
const currencyDecimal = new Intl.NumberFormat(locale).format(1.1).charAt(1);
const isDecimalComma = currencyDecimal === ",";
const currencySymbol = formatCurrency(0, 0).replace("0", "").replace(/\u00A0/, "");
function setUnformattedValue(event) {
  if (event) {
    if (event.key === currencyDecimal) return;
    if (isDecimalComma && event.key === ".")
      formattedValue = formattedValue.replace(/\.([^.]*)$/, currencyDecimal + "$1");
    if (!isDecimalComma && event.key === ",")
      formattedValue = formattedValue.replace(/\,([^,]*)$/, currencyDecimal + "$1");
    const ignoreSymbols = [currencySymbol, `-${currencySymbol}`, "-"];
    const strippedUnformattedValue = formattedValue.replace(" ", "");
    if (ignoreSymbols.includes(strippedUnformattedValue)) return;
    inputTarget = event.target;
    if (isNegativeAllowed && event.key === "-") value = value * -1;
  }
  let unformattedValue = isNegativeAllowed ? formattedValue.replace(/[^0-9,.-]/g, "") : formattedValue.replace(/[^0-9,.]/g, "");
  if (Number.isNaN(parseFloat(unformattedValue))) {
    value = 0;
  } else {
    unformattedValue = unformattedValue.replace(isDecimalComma ? /\./g : /\,/g, "");
    if (isDecimalComma) unformattedValue = unformattedValue.replace(",", ".");
    const previousValue = value;
    value = parseFloat(unformattedValue);
    if (event && previousValue === value) {
      if (unformattedValue.includes(".") && unformattedValue.split(".")[1].length > fractionDigits) {
        setFormattedValue();
      }
    }
  }
}
function setFormattedValue() {
  if (!inputElement) return;
  const startCaretPosition = inputElement.selectionStart || 0;
  const previousFormattedValueLength = formattedValue.length;
  formattedValue = isZero && !isZeroNullish ? "" : formatCurrency(
    value,
    fractionDigits,
    document.activeElement === inputElement ? 0 : fractionDigits
  );
  setUnformattedValue();
  let retries = 0;
  while (previousFormattedValueLength === formattedValue.length && retries < 10) retries++;
  if (previousFormattedValueLength !== formattedValue.length) {
    const endCaretPosition = startCaretPosition + formattedValue.length - previousFormattedValueLength;
    inputElement?.setSelectionRange(endCaretPosition, endCaretPosition);
  }
  onValueChange(value);
}
</script>

<div class={inputClasses?.wrapper ?? DEFAULT_CLASS_WRAPPER}>
	<input
		class={inputClasses?.unformatted ?? DEFAULT_CLASS_UNFORMATTED}
		type="hidden"
		{name}
		{disabled}
		bind:value
	/>
	<input
		class="
			{inputClasses?.formatted ?? DEFAULT_CLASS_FORMATTED}
			{isNegativeAllowed && !isZero && !isNegative
			? inputClasses?.formattedPositive ?? DEFAULT_CLASS_FORMATTED_POSITIVE
			: ''}
			{isZero ? inputClasses?.formattedZero ?? DEFAULT_CLASS_FORMATTED_ZERO : ''}
			{isNegativeAllowed && isNegative
			? inputClasses?.formattedNegative ?? DEFAULT_CLASS_FORMATTED_NEGATIVE
			: ''}
		"
		type="text"
		inputmode={fractionDigits > 0 ? 'decimal' : 'numeric'}
		name={`formatted-${name}`}
		required={required && !isZero}
		placeholder={handlePlaceholder(placeholder)}
		{autocomplete}
		{disabled}
		{id}
    {maxLength}
		bind:this={inputElement}
		bind:value={formattedValue}
		on:keydown={handleKeyDown}
		on:keyup={setUnformattedValue}
		on:blur={setFormattedValue}
	/>
</div>

<style>
	input.currencyInput__formatted {
		border: 1px solid #e2e2e2;
		padding: 10px;
		box-sizing: border-box;
	}

	input.currencyInput__formatted--zero {
		color: #333;
	}

	input.currencyInput__formatted--positive {
		color: #00a36f;
	}

	input.currencyInput__formatted--negative {
		color: #e75258;
	}

	input.currencyInput__formatted:disabled {
		color: #999;
		background-color: #e2e2e2;
		pointer-events: none;
		cursor: default;
	}
</style>
